import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, App, Typography, Space, Tooltip, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { listCategories, createCategory, updateCategory, deleteCategory } from '../services/api';
import { confirmCritical } from '../utils/notifications';
import { useThemeStyles } from '../context/ThemeContext';
import PageHeader from '../components/PageHeader';
import { pageShellStyle } from '../theme';

const { Text } = Typography;
const PAGE_SIZE = 10;

function CategoryManagement() {
  const { message } = App.useApp();
  const { token, isGroupAdmin, isSuperAdmin } = useAuth();
  // Group admins manage roles; super admin sees every admin's roles read-only.
  const canView = isGroupAdmin || isSuperAdmin;
  const readOnly = !isGroupAdmin;
  const themeStyles = useThemeStyles();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchCategories = async () => {
    if (!token || !canView) return;
    setLoading(true);
    try {
      const res = await listCategories(token);
      const rows = Array.isArray(res.data?.categories) ? res.data.categories : [];
      setCategories(rows);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, canView]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredCategories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => {
      const name = String(c.name || '').toLowerCase();
      const desc = String(c.description || '').toLowerCase();
      const creator = String(c.created_by_username || '').toLowerCase();
      return name.includes(q) || desc.includes(q) || creator.includes(q);
    });
  }, [categories, searchQuery]);

  const pagedCategories = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredCategories.slice(start, start + PAGE_SIZE);
  }, [filteredCategories, currentPage]);

  const openCreate = () => {
    setEditingCategory(null);
    form.setFieldsValue({ name: '', description: '' });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingCategory(record);
    form.setFieldsValue({ name: record.name, description: record.description ?? '' });
    setModalOpen(true);
  };

  const onFinish = async (values) => {
    setActionLoading(true);
    try {
      if (editingCategory) {
        await updateCategory(token, editingCategory._id, {
          name: values.name,
          description: values.description || null,
        });
        message.success('Role updated');
      } else {
        await createCategory(token, values.name, values.description);
        message.success('Role created');
      }
      setModalOpen(false);
      fetchCategories();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to save role');
    } finally {
      setActionLoading(false);
    }
  };

  const onDelete = async (categoryId) => {
    try {
      await deleteCategory(token, categoryId);
      message.success('Role deleted');
      fetchCategories();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to delete role');
    }
  };

  const headerCellProps = () => ({
    style: {
      background: themeStyles.userTableHeaderBg,
      fontWeight: 600,
      borderBottom: `1px solid ${themeStyles.userTableHeaderBorder}`,
      color: themeStyles.textPrimary,
      textAlign: 'left',
    },
  });

  if (!canView) {
    return (
      <div style={{ ...pageShellStyle, background: themeStyles.contentBg }}>
        <Text style={{ color: themeStyles.textSecondary }}>Admin access required.</Text>
      </div>
    );
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      onHeaderCell: headerCellProps,
      render: (t) => (
        <Text strong style={{ color: themeStyles.textPrimary, fontSize: 13 }}>
          {t}
        </Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      onHeaderCell: headerCellProps,
      render: (t) => (
        <Text style={{ color: themeStyles.textSecondary, fontSize: 13 }}>
          {t || '—'}
        </Text>
      ),
    },
    {
      title: 'Created by',
      dataIndex: 'created_by_username',
      key: 'created_by',
      width: 180,
      onHeaderCell: headerCellProps,
      render: (t) => (
        <Text style={{ color: themeStyles.textSecondary, fontSize: 13 }}>
          {t || '—'}
        </Text>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      onHeaderCell: headerCellProps,
      render: (t) => {
        const d = t ? new Date(t) : null;
        return (
          <Text style={{ color: themeStyles.textSecondary, fontSize: 13 }}>
            {d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : '—'}
          </Text>
        );
      },
    },
    !readOnly && {
      title: 'Actions',
      key: 'actions',
      width: 120,
      onHeaderCell: headerCellProps,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              aria-label="Edit role"
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              aria-label="Delete role"
              onClick={() => {
                confirmCritical({
                  title: 'Delete role?',
                  content: `Users assigned to "${record.name}" will lose access to files with this role.`,
                  okText: 'Delete',
                  onOk: () => onDelete(record._id),
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ].filter(Boolean);

  return (
    <div style={{ ...pageShellStyle, background: themeStyles.contentBg }}>
      <PageHeader
        title="Document Roles"
        subtitle={
          readOnly
            ? 'Roles created by all admins for file access control. View only — group admins create and edit roles.'
            : 'Create roles for file access control. Assign roles to users and files.'
        }
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: themeStyles.textTertiary }} />}
          placeholder={readOnly ? 'Search roles or admins' : 'Search roles'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        {readOnly ? (
          <Tag icon={<EyeOutlined />} style={{ alignSelf: 'center' }}>
            Read-only
          </Tag>
        ) : (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New role
          </Button>
        )}
      </div>
      <Table
        rowKey={(r) => r._id}
        loading={loading}
        columns={columns}
        dataSource={pagedCategories}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: filteredCategories.length,
          onChange: setCurrentPage,
          showSizeChanger: false,
        }}
        size="small"
      />
      <Modal
        title={editingCategory ? 'Edit role' : 'Create role'}
        open={modalOpen && !readOnly}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={actionLoading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="e.g. HR, Finance, Engineering" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default CategoryManagement;
